import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { RichContent } from "@/components/RichContent";


const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useLanguage();

  const getLocalizedContent = (post: any) => {
    const titleKey = language === 'es' ? 'title' : `title_${language}`;
    const excerptKey = language === 'es' ? 'excerpt' : `excerpt_${language}`;
    const contentKey = language === 'es' ? 'content' : `content_${language}`;
    
    return {
      title: post[titleKey] || post.title,
      excerpt: post[excerptKey] || post.excerpt,
      content: post[contentKey] || post.content
    };
  };

  const translations = {
    en: {
      backToBlog: "Back to Blog"
    },
    es: {
      backToBlog: "Volver al Blog"
    },
    fr: {
      backToBlog: "Retour au Blog"
    }
  };

  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-32 pb-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <Skeleton className="h-12 w-24 mb-8" />
            <Skeleton className="h-16 w-full mb-4" />
            <Skeleton className="h-6 w-3/4 mb-8" />
            <Skeleton className="w-full h-96 mb-8" />
            <Skeleton className="h-4 w-full mb-4" />
            <Skeleton className="h-4 w-full mb-4" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return <Navigate to="/blog" replace />;
  }

  const localizedContent = getLocalizedContent(post);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": localizedContent.title,
    "description": localizedContent.excerpt,
    "image": post.featured_image,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "datePublished": post.published_at,
    "dateModified": post.updated_at
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={`${localizedContent.title} | Shikwaru Adventures Blog`}
        description={localizedContent.excerpt}
        keywords={post.tags?.join(", ") || ""}
        image={post.featured_image}
        structuredData={structuredData}
        canonical={`/blog/${post.slug}`}
      />
      <Navbar />

      <main className="pt-32 pb-20">
        <article className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog">
            <Button variant="ghost" className="mb-8">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {translations[language].backToBlog}
            </Button>
          </Link>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              {post.category && (
                <Badge variant="secondary">{post.category}</Badge>
              )}
              {post.tags && post.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4">{localizedContent.title}</h1>
            
            <p className="text-xl text-muted-foreground mb-6">{localizedContent.excerpt}</p>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center">
                <User size={16} className="mr-2" />
                <span>{post.author}</span>
              </div>
              {post.published_at && (
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  <span>{format(new Date(post.published_at), "MMMM dd, yyyy")}</span>
                </div>
              )}
            </div>
          </header>

          <img
            src={post.featured_image}
            alt={localizedContent.title}
            className="w-full h-auto rounded-lg mb-8 object-cover max-h-[500px]"
          />

          <RichContent
            content={localizedContent.content}
            className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-primary"
          />

        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
